import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import { resolveApiUrl } from '@/lib/api/resolve-url';
import type {
  OcrTemplate,
  OcrTemplateCustomField,
  CreateOcrTemplateRequest,
  UpdateOcrTemplateRequest,
  SyncCustomFieldsRequest,
  SyncFieldRulesRequest,
  OcrTemplatesListParams,
  OcrTemplateArchivedFieldsParams,
  GenerateDescriptionRequest,
  OcrTemplateImportResult
} from '../types';

const { OCR_TEMPLATES } = API_ENDPOINTS;

export const ocrTemplatesApi = {
  getAll: (params?: OcrTemplatesListParams) =>
    apiClient.getPaginated<OcrTemplate>(OCR_TEMPLATES.LIST, params),

  getById: (id: number) => apiClient.get<OcrTemplate>(OCR_TEMPLATES.GET(id)),

  create: (data: CreateOcrTemplateRequest) =>
    apiClient.post<OcrTemplate>(OCR_TEMPLATES.CREATE, data),

  update: (id: number, data: UpdateOcrTemplateRequest) =>
    apiClient.put<OcrTemplate>(OCR_TEMPLATES.UPDATE(id), data),

  delete: (id: number) => apiClient.delete(OCR_TEMPLATES.DELETE(id)),

  bulkDelete: (
    params:
      | { ids: number[] }
      | { all: true; filters: Partial<OcrTemplatesListParams> }
  ) => apiClient.post(OCR_TEMPLATES.BULK_DELETE, params),

  syncCustomFields: (id: number, data: SyncCustomFieldsRequest) =>
    apiClient.put<OcrTemplate>(OCR_TEMPLATES.SYNC_CUSTOM_FIELDS(id), data),

  syncFieldRules: (id: number, data: SyncFieldRulesRequest) =>
    apiClient.put<OcrTemplate>(OCR_TEMPLATES.SYNC_FIELD_RULES(id), data),

  /**
   * OMA-637 — the template's archived fields, read-only. A field removed in
   * the editor lands here on save instead of being deleted.
   */
  getArchivedFields: (id: number, params?: OcrTemplateArchivedFieldsParams) =>
    apiClient.getPaginated<OcrTemplateCustomField>(
      OCR_TEMPLATES.ARCHIVED_FIELDS(id),
      params
    ),

  /**
   * Put an archived field back into the live field set. The server re-checks
   * it against the template's current fields first and rejects a key or label
   * a live field already holds.
   */
  archiveField: (id: number, fieldId: number) =>
    apiClient.post<OcrTemplate>(OCR_TEMPLATES.ARCHIVE_FIELD(id, fieldId)),

  restoreArchivedField: (id: number, fieldId: number) =>
    apiClient.post<OcrTemplate>(
      OCR_TEMPLATES.RESTORE_ARCHIVED_FIELD(id, fieldId)
    ),

  /**
   * OMA-671 — how close a later discovery must come to an archived field
   * before it is suppressed. Null hands the field back to the template's
   * own percentage.
   */
  updateArchivedFieldThreshold: (
    id: number,
    fieldId: number,
    matchThreshold: number | null
  ) =>
    apiClient.patch<OcrTemplateCustomField>(
      OCR_TEMPLATES.ARCHIVED_FIELD_MATCH_THRESHOLD(id, fieldId),
      { match_threshold: matchThreshold }
    ),

  /**
   * AI-author a description from the inputs currently in the form (template
   * name + selected dynamic form + custom field names). Returns the
   * generated text; the caller pastes it into the description input.
   */
  generateDescription: (data: GenerateDescriptionRequest) =>
    apiClient.post<{ description: string }>(
      OCR_TEMPLATES.GENERATE_DESCRIPTION,
      data
    ),

  /**
   * Bulk-create templates from the multi-sheet workbook or an equivalent JSON
   * file. Resolves with a partial-import report — valid templates are created
   * even when other rows fail.
   */
  bulkImport: (file: File, onProgress?: (n: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient
      .uploadFormData<OcrTemplateImportResult>(
        OCR_TEMPLATES.BULK_IMPORT,
        formData,
        onProgress
      )
      .then((r) => r.data);
  },

  /**
   * Download the blank import template, as the XLSX workbook or an equivalent
   * JSON skeleton. Goes through `fetch` rather than apiClient because the
   * response is a binary stream, not the JSON envelope — and it carries
   * Accept-Language so the content comes back in the operator's locale.
   */
  downloadTemplate: async (format: 'xlsx' | 'json' = 'xlsx') => {
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    const locale =
      typeof document !== 'undefined'
        ? (document.documentElement.lang ?? 'en')
        : 'en';
    // gitlab-advanced-sast-exclude -- resolveApiUrl() pins every request to the configured API origin and throws on anything else.
    const res = await fetch(
      resolveApiUrl(`${OCR_TEMPLATES.IMPORT_TEMPLATE}?format=${format}`),
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Accept-Language': locale
        }
      }
    );
    if (!res.ok) throw new Error('Template download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ocr-templates-import-template.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Export real templates as XLSX or JSON, restricted to the same
   * import-compatible subset `bulk-import` accepts — the resulting file can
   * be re-imported as-is. Goes through `fetch` for the same reason as
   * `downloadTemplate`: the response is a binary stream, not the JSON
   * envelope, and needs `Accept-Language` for the localized filename.
   */
  exportTemplates: async (
    format: 'xlsx' | 'json',
    payload:
      | { ids: number[] }
      | { all: true; filters: Partial<OcrTemplatesListParams> }
  ) => {
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    const locale =
      typeof document !== 'undefined'
        ? (document.documentElement.lang ?? 'en')
        : 'en';
    // gitlab-advanced-sast-exclude -- resolveApiUrl() pins every request to the configured API origin and throws on anything else.
    const res = await fetch(
      resolveApiUrl(`${OCR_TEMPLATES.EXPORT}?format=${format}`),
      {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Accept-Language': locale,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ocr-templates-export-${new Date().toISOString().slice(0, 10)}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  }
};
